import React from 'react';
import { SalesforceFieldConfig } from './salesforce';

export enum ColumnType {
  String = 'string',
  Number = 'number',
  Currency = 'currency',
  DateTime = 'datetime',
  Date = 'date',
  Percentage = 'percentage',
  Custom = 'custom',
  Boolean = 'boolean',
  TitleLink = 'title-link',
}

export enum DataType {
  String = 'string',
  Number = 'number',
  Date = 'date',
  List = 'list',
}

export enum ColumnAlignment {
  Left = 'left',
  Right = 'right',
  Center = 'center',
}

export enum ColumnPin {
  Left = 'left',
  Right = 'right',
}

export interface Column {
  id: string;
  name: string;
  columnType: ColumnType;
  // number → px (also becomes a hard min-width in HbrDomTable's setWidth). A string is passed straight
  // through as the CSS width/min-width, so a percentage like '12%' lets the column shrink with the
  // 100%-wide table instead of forcing horizontal scroll.
  width?: number | string | undefined;
  minWidth?: number | undefined;
  maxWidth?: number | undefined;
  align?: ColumnAlignment;
  reorderable?: boolean; // whether the column is reorderable.
  order?: 'asc' | 'desc' | undefined;
  sortable?: boolean; // whether the column is sortable.
  resizable?: boolean; // whether the column is resizable.
  hidden?: boolean;
  queryable?: boolean;
  accessor?: string;
  header?: string;
  className?: string;
  format?: (value: number | string) => string | JSX.Element;
  renderCell?: ({ data, column }: { data: any; column: Column }) => unknown;
  pin?: ColumnPin | undefined; // whether the column is pinned.
  dataType?: DataType; // type of data in the column.
}

const getColumnDataTypeFromColumnType = (columnType: ColumnType): DataType => {
  switch (columnType) {
    case ColumnType.Currency:
    case ColumnType.Number:
    case ColumnType.Percentage:
    case ColumnType.Boolean:
      return DataType.Number;
    case ColumnType.DateTime:
    case ColumnType.Date:
      return DataType.Date;
    default:
      return DataType.String;
  }
}

export const getColumnTypeForPermission = (permission: SalesforceFieldConfig): ColumnType => {
  switch (permission.type) {
    case 'currency':
      return ColumnType.Currency;
    case 'datetime':
      return ColumnType.DateTime;
    case 'date':
      return ColumnType.Date;
    case 'string':
      return ColumnType.String;
    case 'number':
      return ColumnType.Number;
    case 'bool':
      return ColumnType.Boolean;
    case 'percent':
      return ColumnType.Percentage;
    default:
      return ColumnType.String;
  }
};

export const getColumnTypeConfig = (column: Column): Column => {
  const baseConfig: Column = column.columnType !== ColumnType.Custom
    ? {
        id: column.id,
        name: column?.name ?? 'Unlabeled Column',
        columnType: column?.columnType ?? column['type'] ?? ColumnType.String,  // 'type' is deprecated, use 'columnType'
        dataType: column?.dataType ?? getColumnDataTypeFromColumnType(column?.columnType ?? column['type'] ?? ColumnType.String),
        width: column?.width ?? 100,
        minWidth: column?.minWidth ?? undefined,
        maxWidth: column?.maxWidth ?? undefined,
        align: column?.align ?? ColumnAlignment.Left,
        reorderable: column?.reorderable ?? true,
        sortable: column?.sortable ?? true,
        order: column?.order ?? undefined,
        resizable: column?.resizable ?? true,
        pin: undefined,
        format: (value) => value?.toString() ?? '',
      }
    : column;

  switch (baseConfig.columnType) {
    case ColumnType.Currency:
      return {
        ...baseConfig,
        format: (value: number) => 
          <div className="justify-end min-h-8 flex items-center w-full">{value?.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</div>,
      };
    case ColumnType.Date:
    case ColumnType.DateTime:
      return {
        ...baseConfig,
        format: (value: string) => {
          let displayString = '';
          if (value) {
            displayString = new Date(value).toLocaleDateString('en-US');
          }
          return <div className="min-h-8 flex items-center">{displayString}</div>;
        },
      };
    default:
      return baseConfig;
  }
}